import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import AdminNav from "@/components/AdminNav";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

type Submission = {
  id: string;
  answer: string;
  ip: string;
  ua?: string;
  status: "queued" | "approved" | "declined";
  createdAt: number;
  moderatedBy?: string;
  moderatedAt?: number;
};

export default function Admin() {
  const { user } = useAuth();
  const [subs, setSubs] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [accepting, setAccepting] = useState<boolean>(true);
  const [snippets, setSnippets] = useState<Record<string, string>>({});
  const [selectedSnippet, setSelectedSnippet] = useState<string | null>(null);
  const [editingSnippetContent, setEditingSnippetContent] = useState<string>("");
  const [savingAccepting, setSavingAccepting] = useState(false);
  const [savingSnippet, setSavingSnippet] = useState(false);

  const fetchAll = async (pageArg?: number, perPageArg?: number) => {
    const p = pageArg ?? page;
    const pp = perPageArg ?? perPage;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (q) params.set("q", q);
      params.set("page", String(p));
      params.set("perPage", String(pp));
      const url = `/api/admin/submissions?${params.toString()}`;
      const res = await fetch(url);
      if (!res.ok) {
        setSubs([]);
        setTotal(0);
        return;
      }
      const data = await res.json();
      setSubs(data.submissions ?? []);
      setTotal(data.total ?? (data.submissions ?? []).length);
      setPage(data.page ?? p);
      setPerPage(data.perPage ?? pp);
    } catch (e) {
      setSubs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll(1, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchAccepting = async () => {
      try {
        const res = await fetch("/api/admin/accepting");
        if (!res.ok) return;
        const data = await res.json();
        if (!mounted) return;
        setAccepting(Boolean(data.accepting));
        const snippetKey = data.snippetKey ?? null;

        // fetch snippets
        try {
          const sres = await fetch("/api/snippets");
          if (sres.ok) {
            const sdata = await sres.json();
            setSnippets(sdata ?? {});
            const keys = Object.keys(sdata ?? {});
            const keyToUse = snippetKey ?? (keys.length ? keys[0] : null);
            setSelectedSnippet(keyToUse);
            setEditingSnippetContent(keyToUse ? (sdata[keyToUse] ?? "") : String(data.message ?? ""));
          } else {
            setSnippets({});
            setSelectedSnippet(null);
            setEditingSnippetContent(String(data.message ?? ""));
          }
        } catch (e) {
          setSnippets({});
          setSelectedSnippet(null);
          setEditingSnippetContent(String(data.message ?? ""));
        }
      } catch (e) {
        // ignore
      }
    };
    fetchAccepting();
    return () => { mounted = false; };
  }, []);

  const saveSnippet = async () => {
    if (!selectedSnippet) return;
    setSavingSnippet(true);
    try {
      const res = await fetch("/api/admin/snippets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: selectedSnippet, content: editingSnippetContent }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data?.message ?? "Failed to save snippet");
        return;
      }
      setSnippets((s) => ({ ...s, [selectedSnippet]: editingSnippetContent }));
    } catch (e) {
      alert("Failed to save snippet");
    } finally {
      setSavingSnippet(false);
    }
  };

  const saveAccepting = async () => {
    setSavingAccepting(true);
    try {
      const res = await fetch("/api/admin/accepting", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accepting, snippetKey: selectedSnippet }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data?.message ?? "Failed to save settings");
      }
    } catch (e) {
      alert("Failed to save settings");
    } finally {
      setSavingAccepting(false);
    }
  };

  const applyFilters = () => {
    setPage(1);
    fetchAll(1, perPage);
  };

  const doAction = async (id: string, action: "approve" | "decline") => {
    setActionLoading((s) => ({ ...s, [id]: true }));
    try {
      const res = await fetch("/api/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, action }),
      });
      if (res.ok) {
        setSubs((s) =>
          s.map((sub) => (sub.id === id ? { ...sub, status: action === "approve" ? "approved" : "declined", moderatedBy: user?.username, moderatedAt: Date.now() } : sub)),
        );
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data?.message ?? "Action failed");
      }
    } catch (e) {
      alert("Action failed");
    } finally {
      setActionLoading((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <>
      <AdminNav />
      <div className="min-h-screen p-8 bg-background">
        <div className="max-w-6xl mx-auto mt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold">Admin — All Submissions</h1>
              <p className="text-sm text-muted-foreground">View and filter all submissions (queued, approved, declined).</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => fetchAll(page, perPage)}>Reload</Button>
            </div>
          </div>

          <Card className="mb-4">
            <CardHeader>
              <CardTitle>Site controls</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3">
                  <Label>Accept submissions</Label>
                  <Switch checked={accepting} onCheckedChange={(v) => setAccepting(Boolean(v))} />
                </div>

                <div className="flex-1 grid gap-2">
                  <Label>Closed message snippet</Label>
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedSnippet ?? ""}
                      onChange={(e) => { setSelectedSnippet(e.target.value); setEditingSnippetContent(snippets[e.target.value] ?? ""); }}
                      className="rounded-md border px-2 py-1"
                    >
                      {Object.keys(snippets).map((k) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                    <Button size="sm" onClick={saveSnippet} disabled={savingSnippet}>Save snippet</Button>
                  </div>
                  <textarea value={editingSnippetContent} onChange={(e) => setEditingSnippetContent(e.target.value)} className="mt-2 w-full rounded-md border p-2" />
                </div>

                <div>
                  <Button size="sm" onClick={saveAccepting} disabled={savingAccepting}>Save</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <Label>Status</Label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-md border px-2 py-1">
                  <option value="all">All</option>
                  <option value="queued">Queued</option>
                  <option value="approved">Approved</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
              <Input placeholder="Search text / IP / UA" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1" />
              <div className="flex gap-2">
                <Button variant="default" size="sm" onClick={applyFilters} disabled={loading}>Apply</Button>
                <Button variant="ghost" size="sm" onClick={() => { setQ(""); setStatusFilter("all"); setPage(1); fetchAll(1, perPage); }}>Reset</Button>
              </div>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Submissions ({total})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? <div>Loading...</div> : null}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="px-2 py-2">Answer</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-2 py-2">IP</th>
                      <th className="px-2 py-2">UA</th>
                      <th className="px-2 py-2">Created</th>
                      <th className="px-2 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subs.map((s) => (
                      <tr key={s.id} className="border-t">
                        <td className="px-2 py-2 max-w-[400px] truncate">{s.answer}</td>
                        <td className="px-2 py-2 text-xs">{s.status}</td>
                        <td className="px-2 py-2 text-xs">{s.ip}</td>
                        <td className="px-2 py-2 max-w-[300px] truncate text-xs">{s.ua}</td>
                        <td className="px-2 py-2 text-xs">{new Date(s.createdAt).toLocaleString()}</td>
                        <td className="px-2 py-2">
                          <div className="flex gap-2">
                            <Button size="sm" variant="secondary" onClick={() => doAction(s.id, "approve")} disabled={!!actionLoading[s.id] || s.status === "approved"}>Approve</Button>
                            <Button size="sm" variant="destructive" onClick={() => doAction(s.id, "decline")} disabled={!!actionLoading[s.id] || s.status === "declined"}>Decline</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {total === 0 ? 0 : Math.min((page - 1) * perPage + 1, total)} - {Math.min(page * perPage, total)} of {total}
                </div>
                <div className="flex items-center gap-2">
                  <select value={perPage} onChange={(e) => { const pp = parseInt(e.target.value, 10) || 20; setPerPage(pp); setPage(1); fetchAll(1, pp); }} className="rounded-md border px-2 py-1 text-sm">
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <Button size="sm" onClick={() => { if (page > 1) { const np = page - 1; setPage(np); fetchAll(np, perPage); } }} disabled={page <= 1}>Prev</Button>
                  <div className="text-sm">Page {page} / {Math.max(1, Math.ceil(total / perPage))}</div>
                  <Button size="sm" onClick={() => { const last = Math.max(1, Math.ceil(total / perPage)); if (page < last) { const np = page + 1; setPage(np); fetchAll(np, perPage); } }} disabled={page >= Math.max(1, Math.ceil(total / perPage))}>Next</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
